import { isNumber } from 'lodash';
import React from 'react';
import { View as RNView, StyleProp, ViewProperties, ViewStyle } from 'react-native';
import { SharedStyles, grid } from '@shared/styles';
import { Styles } from './styles';

export type MBViewPadding = number;

export interface MBViewRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface MBViewProps {
  /*
        Make the view match its parent's width/height
    */
  fill?: boolean;

  /*
        100% width
    */
  fullWidth?: boolean;

  /*
        100% height
    */
  fullHeight?: boolean;

  /*
        Center align children vertically and horizontally
    */
  center?: boolean;

  /*
        Center aligns children vertically
    */
  centerVertically?: boolean;

  /*
        Center aligns children horizontally
    */
  centerHorizontally?: boolean;

  /*
        Make the view absolutely positioned to cover its parent
    */
  absolute?: boolean;

  /*
        Wraps the views width (for parent flex direction col)
        or height (for parent flex direction row) instead of
        filling the available space.
    */
  wrap?: boolean;

  /*
        Easy to use props to give views standardized paddings
    */
  padding?: MBViewPadding;
  paddingTop?: MBViewPadding;
  paddingLeft?: MBViewPadding;
  paddingRight?: MBViewPadding;
  paddingBottom?: MBViewPadding;
  paddingVertical?: MBViewPadding;
  paddingHorizontal?: MBViewPadding;

  /*
        Flex direction row makes children align in rows. If set to "reverse",
        it will set flex direction to row-reverse
    */
  row?: boolean | 'reverse';

  /*
        Set pointerEvents to "none" to block all the view's touch events
    */
  pointerEvents?: ViewProperties['pointerEvents'];

  /*
        How much should the view flex. If true, flex = 1
        Handy when having to show multiple views inside the
        same parent with different % of the height
    */
  flex?: boolean | number;
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode | React.ReactNode[];
  testID?: string;
  /*
       Used to give DOM elements ID on web
    */
  nativeID?: string;
}

// Default the flex (otherwise it's weird on web). react-native-web accepts
// null even though RN's ViewStyle type doesn't, hence the cast.
const flexReset = { flex: null } as unknown as ViewStyle;
// react-native-web gives all views z-index 0 as default. This causes issues
// when trying to overlay content in nested views. Resetting to no z-index
// makes the HTML renderer figure out the view hierarchy better. 'unset' is
// web-only, hence the cast.
const zIndexReset = { zIndex: 'unset' } as unknown as ViewStyle;

// Note(fant): the reason we've done MBView as a component and not an SFC is because
// we really want to avoid providing arrow functions as props since that would cause
// the components and children to re-render every time some prop is changed. This
// is really important for performance reasons. In other parts of the shared components
// we're a little sloppy with this, which is not great. But for these most fundamental
// components it's crucial.
export class MBView extends React.PureComponent<MBViewProps> {
  render() {
    const {
      testID,
      nativeID,
      row,
      wrap,
      fill,
      center,
      absolute,
      fullWidth,
      fullHeight,
      flex,
      centerVertically,
      centerHorizontally,
      padding,
      paddingTop,
      paddingLeft,
      paddingRight,
      paddingBottom,
      paddingVertical,
      paddingHorizontal,
      style,
      pointerEvents,
      children,
    } = this.props;
    return (
      <RNView
        testID={testID}
        nativeID={nativeID}
        style={[
          flexReset,
          zIndexReset,
          row === true && Styles.row,
          row === 'reverse' && Styles.rowReverse,
          wrap && Styles.wrap,
          fill && SharedStyles.fill,
          center && SharedStyles.centerChildren,
          absolute && SharedStyles.absoluteCover,
          fullWidth && { width: '100%' },
          fullHeight && { height: '100%' },
          // if flex is a bool: Number(true) => 1
          // if flex is a number: Number(val) => val
          !!flex && { flex: Number(flex) },

          !row && centerVertically && { justifyContent: 'center' },
          !row && centerHorizontally && { alignItems: 'center' },
          row && centerVertically && { alignItems: 'center' },
          row && centerHorizontally && { justifyContent: 'center' },

          isNumber(padding) && { padding: grid(padding) },
          isNumber(paddingTop) && { paddingTop: grid(paddingTop) },
          isNumber(paddingLeft) && { paddingLeft: grid(paddingLeft) },
          isNumber(paddingRight) && {
            paddingRight: grid(paddingRight),
          },
          isNumber(paddingBottom) && {
            paddingBottom: grid(paddingBottom),
          },
          isNumber(paddingVertical) && {
            paddingTop: grid(paddingVertical),
            paddingBottom: grid(paddingVertical),
          },
          isNumber(paddingHorizontal) && {
            paddingLeft: grid(paddingHorizontal),
            paddingRight: grid(paddingHorizontal),
          },

          style,
        ]}
        pointerEvents={pointerEvents}
      >
        {children}
      </RNView>
    );
  }
}
